import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

export interface ReviewManagementTileFilter {
  ReviewTypeID?: string;
  ReviewStatus?: string;
  OverallReportRating?: string;
}

interface ReviewTypeTile {
  count: number;
  label: string;
  reviewTypeId: string;
  tooltipLines?: string[];
}

@Component({
  standalone: true,
  selector: 'app-review-management-dashboard',
  imports: [CommonModule, RouterLink],
  templateUrl: './review-management-dashboard.component.html',
  styleUrl: './review-management-dashboard.component.css',
})
export class ReviewManagementDashboardComponent {
  @Output() tileClick = new EventEmitter<ReviewManagementTileFilter>();

  reviewTypes: ReviewTypeTile[] = [
    { count: 0, label: 'Standard Reviews', reviewTypeId: '1' },
    { count: 0, label: 'Go-Live Reviews', reviewTypeId: '2' },
    { count: 0, label: 'On Demand Reviews', reviewTypeId: '3' },
    { count: 0, label: 'Project-Based Reviews', reviewTypeId: '4' },
  ];
  totalReviewsTooltipLines: string[] = [];

  private apiTotalReviews: number | null = null;
  private _specialProjectLines: string[] = [];

  /** Review distribution counts from GetReviewDashboardCounts. */
  @Input() set reviewDistribution(value: any) {
    const d = Array.isArray(value) ? value[0] : value;
    if (!d) return;
    this.reviewTypes = [
      { count: d.StandardReviewCount ?? 0, label: 'Standard Reviews', reviewTypeId: '1' },
      { count: d.GoLiveReviewCount ?? 0, label: 'Go-Live Reviews', reviewTypeId: '2' },
      { count: d.OnDemandReviewCount ?? 0, label: 'On Demand Reviews', reviewTypeId: '3' },
      { count: d.SpecialProjectReviewCount ?? 0, label: 'Project-Based Reviews', reviewTypeId: '4', tooltipLines: this._specialProjectLines },
    ];
    this.apiTotalReviews = d.TotalReviews ?? null;
    this.totalReviewsTooltipLines = [
      `Standard: ${d.StandardReviewCount ?? 0}  ·  Go-Live: ${d.GoLiveReviewCount ?? 0}`,
      `On-Demand: ${d.OnDemandReviewCount ?? 0}  ·  Project-Based: ${d.SpecialProjectReviewCount ?? 0}`,
    ];
  }

  /** SpecialProjectCount array from GetReviewDashboardCounts — drives the Project-Based Reviews tooltip. */
  @Input() set specialProjectCount(value: any[]) {
    const list = Array.isArray(value) ? value : [];
    const nonZero = list.filter(p => (p.TypeOfProjectCount ?? 0) > 0);
    const lines: string[] = [];
    for (let i = 0; i < nonZero.length; i += 2) {
      const a = `${nonZero[i].ShortCode}: ${nonZero[i].TypeOfProjectCount}`;
      const b = nonZero[i + 1] ? `  ·  ${nonZero[i + 1].ShortCode}: ${nonZero[i + 1].TypeOfProjectCount}` : '';
      lines.push(a + b);
    }
    this._specialProjectLines = lines;
    const pbEntry = this.reviewTypes.find(t => t.label === 'Project-Based Reviews');
    if (pbEntry) pbEntry.tooltipLines = lines;
  }

  private _rawStatusCounts: any[] = [];
  private _rawCategoryCounts: any[] = [];

  /** Review status counts from GetReviewDashboardCounts. */
  @Input() set reviewStatusCounts(value: any[]) {
    this._rawStatusCounts = Array.isArray(value) ? value : [];
    this.buildReviewStatuses();
  }

  /** ReviewStatusCategoriesCounts from GetReviewDashboardCounts — drives sub-status tooltips. */
  @Input() set reviewStatusCategoriesCounts(value: any[]) {
    this._rawCategoryCounts = Array.isArray(value) ? value : [];
    this.buildReviewStatuses();
  }

  private buildReviewStatuses(): void {
    // Maps each category prefix to its parent status label
    const categoryToStatus: { prefix: string; status: string }[] = [
      { prefix: 'PL',                status: 'Planning' },
      { prefix: 'SCP',               status: 'Planning' },
      { prefix: 'FW',                status: 'Conduct Review' },
      { prefix: 'Pre-Go-Live FW',    status: 'Conduct Review' },
      { prefix: 'Interim Call Held', status: 'Conduct Review' },
      { prefix: 'POST-Go-Live FW',   status: 'Conduct Review' },
      { prefix: 'RPTG',              status: 'Drafting Report' },
      { prefix: 'IM',                status: 'Report Published' },
    ];

    // Group sub-statuses by parent status label
    const subStatusMap: Record<string, Array<{ label: string; count: number }>> = {};
    for (const cat of this._rawCategoryCounts) {
      const catLabel: string = cat.ReviewStatusCategory ?? '';
      const catCount: number = cat.ReviewStatusCategoryCount ?? 0;
      const match = categoryToStatus.find(m =>
        catLabel === m.prefix ||
        catLabel.startsWith(m.prefix + ' ') ||
        catLabel.startsWith(m.prefix + '-')
      );
      if (match) {
        if (!subStatusMap[match.status]) subStatusMap[match.status] = [];
        subStatusMap[match.status].push({ label: catLabel, count: catCount });
      }
    }

    const statusCountMap: Record<string, number> = {};
    for (const s of this._rawStatusCounts) {
      const label = String(s.ReviewStatus ?? '').replace(/([a-z])([A-Z])/g, '$1 $2');
      statusCountMap[label] = s.ReviewStatusCount ?? 0;
    }

    this.reviewStatuses = ['Planning', 'Conduct Review', 'Drafting Report', 'Report Published'].map(label => ({
      count: statusCountMap[label] ?? 0,
      label,
      subStatuses: label === 'Report Published'
        ? (subStatusMap[label] ?? []).slice().reverse()
        : subStatusMap[label] ?? [],
    }));
  }

  get totalReviews(): number {
    return this.apiTotalReviews ?? this.reviewTypes.reduce((sum, t) => sum + t.count, 0);
  }

  selectReviewType(reviewTypeId: string): void {
    this.tileClick.emit({ ReviewTypeID: reviewTypeId });
  }

  selectTotalReviews(): void {
    this.tileClick.emit({});
  }

  selectReviewStatus(reviewStatus: string): void {
    this.tileClick.emit({ ReviewStatus: reviewStatus });
  }

  selectReviewRating(overallReportRating: string): void {
    this.tileClick.emit({ OverallReportRating: overallReportRating });
  }

  reviewStatuses: Array<{ count: number; label: string; subStatuses: Array<{ label: string; count: number }> }> = [
    { count: 0, label: 'Planning', subStatuses: [] },
    { count: 0, label: 'Conduct Review', subStatuses: [] },
    { count: 0, label: 'Drafting Report', subStatuses: [] },
    { count: 0, label: 'Report Published', subStatuses: [] },
  ];

  reportRatings: Array<{ count: number; label: string; color: string; darkText: boolean }> = [
    { count: 0, label: 'Red', color: '#EB3323', darkText: false },
    { count: 0, label: 'Yellow', color: '#F6C143', darkText: true },
    { count: 0, label: 'Blue', color: '#93AAD7', darkText: true },
    { count: 0, label: 'Green', color: '#167C61', darkText: false },
  ];

  /** Overall report review rating counts from GetReviewDashboardCounts. */
  @Input() set overallReportReviewRating(value: any) {
    const d = Array.isArray(value) ? value[0] : value;
    if (!d) return;
    const styleByRating: Record<string, { color: string; darkText: boolean }> = {
      Red:    { color: '#EB3323', darkText: false },
      Yellow: { color: '#F6C143', darkText: true  },
      Blue:   { color: '#93AAD7', darkText: true  },
      Green:  { color: '#167C61', darkText: false },
    };
    this.reportRatings = (['Red', 'Yellow', 'Blue', 'Green'] as const).map(key => ({
      count: d[key] ?? 0,
      label: key,
      color: styleByRating[key].color,
      darkText: styleByRating[key].darkText,
    }));
  }
}
